/**
 * Data needs to be in many shapes for the charts. This module produces them
 */

import flatten from 'lodash/flatten'
import { ensureArray } from 'lib/array'
import { getShades } from './config/colors'
import colors, { getCorrelationFill } from 'lib/components/charts/colors'

function reduceSeries(series, fn) {
  return ensureArray(series).reduce(function(memo, group) {
    let values = group.dataPoints.map(fn)

    return memo.concat(values)
  }, [])
}

export function scatterplot(data, x, y) {
  return data.map(point => {
    const isBenchmark = point.Category === 'Benchmark'

    return {
      id: point.Key,
      x: point[x] || 0,
      y: point[y] || 0,
      isActive: point.isActive,
      isFocused: point.isFocused,
      isHollow: point.Category === 'Small',
      category: point.Category,
      label: point.Label,
      primary: point.Name,
      // The Large Cap "Benchmark" should be a black diamond
      // https    : //github.com/vigetlabs/research-affiliates-dashboard/issues/56
      fill: point.Color,
      isBenchmark: isBenchmark
    }
  })
}

export function valuationByType(data, type) {
  // type: Agg or PB
  const items = data.filter(d => d.Category !== 'Benchmark')

  return items.map(point => {
    return {
      key: point.Key,
      id: point.Key,
      isActive: point.isActive,
      isFocused: point.isFocused,
      category: point.Category,
      categoryShortName: point.CategoryShortName,
      fill: point.Color,
      label: point.Label,
      primary: point.Label,
      shortName: point.ShortName,
      lower: point[`RelVal${type}10th`] || 0,
      upper: point[`RelVal${type}90th`] || 0,
      median: point[`RelVal${type}50th`] || 0,
      current: point[`CurrentRelVal${type}`] || 0
    }
  })
}

export function strategy(data, type) {
  const items = data.filter(d => d.Label && d.Category !== 'Benchmark')

  return items.map(function(point) {
    return {
      id: point.Key,
      x: point.Name,
      y: point[type] || 0,
      isActive: point.isActive,
      isFocused: point.isFocused,
      category: point.Category,
      categoryShortName: point.CategoryShortName,
      shortName: point.ShortName,
      label: point.Label,
      primary: point.Name,
      fill: point.Color
    }
  })
}

export function performanceHistory(
  series,
  { withValuation = false },
  yProperty
) {
  if (series.length <= 0) {
    return []
  }

  let shades = getShades(series, 'Name')

  // Only include series that have data
  let havePoints = series.filter(group => group.dataPoints.length)

  let datasets = havePoints.map(function(group) {
    return {
      key: group.Key,
      label: group.Label,
      color: shades[group.Name],
      points: group.dataPoints.map(function(point) {
        return {
          x: new Date(point.DataDate),
          y: point[yProperty],
          y2: point.RelValAgg
        }
      })
    }
  })

  if (withValuation) {
    datasets.push({
      key: 'relValAgg',
      label: 'Valuation',
      color: colors.mediumGray,
      points: reduceSeries(series, function(point) {
        return {
          x: new Date(point.DataDate),
          y: point.RelValAgg,
          y2: point.RelValAgg
        }
      })
    })
  }

  return datasets
}

export function model(series) {
  let { dataPoints, Color } = series.length ? series[0] : { dataPoints: [] }

  return dataPoints
    .map(function(point) {
      return {
        x: point.RelValAgg,
        y: point.Subsequent5YR,
        label: point.DataDate,
        fill: Color
      }
    })
    .sort((a, b) => (a.x > b.x ? 1 : -1))
}

export function modelHistory(series) {
  if (series.length <= 0) {
    return []
  }

  let { dataPoints, Label, Color, Key } = series[0]

  const subsequentReturn = dataPoints.map(function(point) {
    return {
      x: new Date(point.DataDate),
      y: point.Subsequent5YR
    }
  })

  const expectedReturn = dataPoints.map(function(point) {
    return {
      x: new Date(point.DataDate),
      y: point.Exp5YRExReturn
    }
  })

  return [
    {
      key: Key,
      label: Label,
      color: Color,
      points: subsequentReturn
    },
    {
      key: Key + '-valuation',
      label: Label + ' Expected Excess Returns Over Market Benchmark',
      color: colors.mediumGray,
      points: expectedReturn
    }
  ]
}

export function correlation(factors, factorCorrelations, options = {}) {
  const filtered = factors.map(factor1 => {
    return factors.map(factor2 => {
      const point = factorCorrelations.find(
        c => c.ID1 === factor1.ID && c.ID2 === factor2.ID
      )

      // bail if no point found based on provided factor IDs
      if (!point) return null

      const { selected, category, expanded, filter } = options

      const isInFilterCategory =
        factor1.Category === filter && factor2.Category === filter
      const isInSelectedFactor =
        factor1.Key === selected || factor2.Key === selected
      const isInSelectedCategory = factor1.isActive || factor2.isActive

      if (!expanded && !isInFilterCategory) {
        return null
      }

      let desaturated = false
      let label = null
      let opacity = 1
      let x = factor1.ShortName
      let y = factor2.ShortName
      let key = `${factor1.CategoryShortName}:${x}-${factor1.CategoryShortName}:${y}`
      let correlation = point.Correlation

      // when expanded
      if (expanded) {
        // x and y must be unique across both categories' factors
        x = `${factor1.ShortName} (${factor1.CategoryShortName})`
        y = `${factor2.ShortName} (${factor2.CategoryShortName})`
        key = `${x}-${y}`

        // when a factor is selected, highlight dots in factor row/column OR
        // when a category is selected, highlight dots that are in that category
        const selectedOpacity = isInSelectedFactor ? 1 : 0.3
        const categoryOpacity = isInSelectedCategory ? 1 : 0.3

        desaturated =
          (selected && !isInSelectedFactor) ||
          (category && !isInSelectedCategory)
        opacity = selected
          ? selectedOpacity
          : category
          ? categoryOpacity
          : opacity
        label = parseFloat(point.Correlation).toFixed(2)
      }

      let fill = getCorrelationFill(point.Correlation, desaturated)

      return { x, y, key, opacity, correlation, fill, label }
    })
  })

  // flatten the nested array produced above and filter out non-truthy values
  return flatten(filtered).filter(value => !!value)
}



// WEBPACK FOOTER //
// ./src/parse.js